<script>
  import {mapState, mapGetters, mapActions} from 'vuex';
  import P from '~/lib/promise';
  import constants from '~/lib/domain/constants';
  import format from '~/lib/domain/format';
  import lang from '~/lib/lang';
  import CustomerContext from './CustomerContext.vue';
  import PropertyContext from './PropertyContext.vue';

  export default {

    mixins: [CustomerContext, PropertyContext],

    computed: {

      ...mapGetters('job/record', {
        job: 'props',
      }),

      ...mapGetters('job/datatable', {
        jobTable: 'props',
      }),

      ...mapGetters('job/search', {
        'jobSearch': 'props',
      }),

      ...mapGetters('document/datatable', {
        documentTable: 'props',
      }),

      ...mapGetters('job/notes', {
        jobNotes: 'props',
      }),

      ...mapGetters('job/attachments', {
        jobAttachments: 'props',
      }),

      currentJobState () {
        let current = [...this.job.attributes.states].pop();
        return current ? current.name : null;
      },

      /**
       * The status the job table is currently filtered to, or '' for none.
       *
       * Reads straight from the module rather than page-local state, so a
       * selection restored from storage shows up in the control without the
       * page tracking it. The filter is stored as an array because that is
       * what the API takes -- `?filter=match_states:[quoted]` -- so unwrap
       * the first element for a control that offers one at a time.
       */
      jobTableState () {
        let states = this.jobTable.filters[constants.JOB_FILTER_STATES] || [];
        return states[0] || '';
      },

      jobStateButtonDefinitions () {
        let colors = this.jobStateButtonColors;
        let {transitions} = this.job.attributes;
        return lang.collection.map(constants.JOB_STATES, (label, value) => {
          return {
            label,
            value,
            color: 'is-warning',
            disabled: value !== this.currentJobState && transitions.indexOf(value) === -1,
          };
        });
      },

      jobStateButtonColors () {
        return {
          [constants.JOB_STATE_OPEN]: 'is-light',
          [constants.JOB_STATE_ESTIMATED]: 'is-light',
          [constants.JOB_STATE_QUOTED]: 'is-warning',
          [constants.JOB_STATE_AWARDED]: 'is-warning',
          [constants.JOB_STATE_SCHEDULED]: 'is-warning',
          [constants.JOB_STATE_PROGRESS]: 'is-success',
          [constants.JOB_STATE_COMPLETED]: 'is-success',
          [constants.JOB_STATE_CLOSED]: 'is-dark',
        };
      },

      jobContact () {
        if (!this.job.attributes) {
          return null;
        }
        let {contact} = this.job.attributes;

        if (!contact) {
          return null;
        }

        let keys = [
          'firstname',
          'lastname',
          'description',
          'email',
          'tel',
          'tel_secondary',
        ];

        let entries = keys.reduce((out, k) => {
          let v = contact[k];
          v && out.push([k, v]);
          return out;
        }, []);

        return entries.length ? lang.object.fromEntries(entries) : null;
      },
    },

    methods: {

      ...mapActions('job/datatable', {
        initializeJobTable: 'initialize',
        loadJobTable: 'load',
        setJobTablePage: 'setPage',
        setJobTableLimit: 'setLimit',
        setJobTableQuery: 'setQuery',
        setJobTableFilter: 'setFilter',
      }),

      ...mapActions('job/record', {
        initializeJob: 'initialize',
        loadJob: 'load',
        saveJob: 'save',
        deleteJob: 'delete',
        assignJobAttributes: 'assign',
        propagateJobObjects: 'propagate',
        deleteJobContact: 'deleteContact',
        assignJobFormAttributes: 'assignFormAttributes',
        resetJobForm: 'resetForm',
        attachJobBlob: 'attach',
        detachJobBlob: 'detach',
        detachAllJobBlobs: 'detachAll',
      }),

      ...mapActions('job/search', {
        initializeJobSearch: 'initialize',
        resetJobSearch: 'reset',
        queryJobSearch: 'query',
      }),

      ...mapActions('document/datatable', {
        initializeDocumentTable: 'initialize',
        setDocumentTableQuery: 'setQuery',
      }),

      ...mapActions('job/notes', {
        initializeJobNotes: 'initialize',
        loadJobNotes: 'loadNotes',
        addJobNote: 'addNote',
        deleteJobNote: 'deleteNote',
      }),

      ...mapActions('job/attachments', {
        initializeJobAttachments: 'initialize',
        updateJobAttachment: 'updateAttachment',
      }),

      /**
       * Clear every module this page reads, including the customer and
       * property modules the job pages display alongside the job.
       *
       * Synchronous by design; see resetCustomerContext for why that matters.
       *
       * Note what is absent: the document table. A page that shows documents
       * scopes them to its own job, and resetting the table here would race
       * that page's own initialize call -- both ran inside the same P.all
       * with no ordering between them, so an unscoped reset could land last
       * and discard the page's filter. Pages own that table; see
       * JobDetailPage and JobSheetPage.
       */
      resetJobContext () {
        let id = this.guessJobIdFromRoute();

        this.resetCustomerContext();
        this.resetPropertyContext();
        this.initializeJob();
        this.initializeJobTable({url: '/jobs'});
        this.initializeJobNotes({subject: id});
        this.initializeJobAttachments();
        this.initializeJobSearch();
      },

      async initializeJobContext () {
        this.resetJobContext();

        let id = this.guessJobIdFromRoute();
        if (id) {
          await P.all([
            this.loadJob({id}),
            this.loadJobNotes(),
          ]);
        }

      },

      async commitJobEdit () {
        let ok = await this.saveJob();
        if (ok) {
          this.$router.replace({
            name: 'job:detail',
            params: {
              id: this.job.attributes.id,
            },
          });
        }
      },

      async cancelJobEdit () {
        this.$router.go(-1);
      },

      guessJobIdFromRoute () {
        let {name, params, query} = this.$route;
        if (params.id) {
          if (name.match(/^job:(detail|edit|sheet)/) || name.match(/^document:create$/)) {
            return params.id;
          }
        }
        if (query.job) {
          return query.job;
        }
        return null;
      },

      navigateToJob (id) {
        if (id) {
          this.$router.push({name: 'job:detail', params:{id}});
        }
      },

      async attemptJobStateTransition (next) {
        this.assignJobFormAttributes({state: next});
        await this.saveJob();
        await this.loadJob({id: this.job.attributes.id});
      },

      async handleJobSearchTerm (terms) {
        await this.queryJobSearch({terms});
      },

      /**
       * Apply a status selection to the job table and remember it.
       *
       * Wraps the single value in an array for the API. An empty string
       * means "all statuses", and setFilter drops the key entirely on an
       * empty array, so no filter is sent at all.
       *
       * @param {String} state  a JOB_STATES key, or '' for no filter
       */
      async handleJobStatusFilter (state) {
        await this.setJobTableFilter({
          key: constants.JOB_FILTER_STATES,
          value: state ? [state] : [],
        });
      },

    },
  };

</script>
